import React, { useRef } from 'react';
import RawHTML from '~/components/jsx-helpers/raw-html';
import useDropdownContext from '../../dropdown-context';
import useWindowContext from '~/contexts/window';
import useNavigateByKey from './use-navigate-by-key';
import useMenuControls from './use-menu-controls';
import { treatSpaceOrEnterAsClick } from '~/helpers/events';
import { useLocation } from 'react-router-dom';
import usePortalContext from '~/contexts/portal';
import cn from 'classnames';
import './dropdown.scss';
// Using ARIA Disclosure pattern rather than Menubar pattern
// because menubar requires complexity that is not necessary
// for ordinary website navigations, per
// https://www.w3.org/WAI/ARIA/apg/patterns/menubar/examples/menubar-navigation/
export function MenuItem({ label, url, local = undefined }) {
    const { innerWidth: _ } = useWindowContext();
    const urlPath = url.replace('/view-all', '');
    const { pathname } = useLocation();
    const { portalPrefix } = usePortalContext();
    return (React.createElement(RawHTML, Object.assign({ Tag: "a", html: label, href: `${portalPrefix}${url}`, tabIndex: 0, "data-local": local }, (urlPath === pathname ? { 'aria-current': 'page' } : {}))));
}
function OptionalWrapper({ isWrapper, children }) {
    return isWrapper ? (React.createElement("div", { className: "nav-menu-item dropdown" }, children)) : (children);
}
export default function Dropdown({ Tag = 'li', className = undefined, label, children, excludeWrapper = false, navAnalytics }) {
    const topRef = useRef(null);
    const dropdownRef = useRef(null);
    const ddId = `ddId-${label}`;
    const { closeMenu, closeDesktopMenu, openMenu, openDesktopMenu } = useMenuControls({ topRef, label });
    const navigateByKey = useNavigateByKey({
        topRef,
        dropdownRef,
        closeMenu,
        closeDesktopMenu
    });
    const closeOnBlur = React.useCallback((event) => {
        const { currentTarget, relatedTarget } = event;
        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            closeDesktopMenu();
        }
    }, [closeDesktopMenu]);
    return (React.createElement(Tag, { className: className, onMouseEnter: openDesktopMenu, onMouseLeave: closeDesktopMenu, onKeyDown: navigateByKey, onBlur: closeOnBlur },
        React.createElement(OptionalWrapper, { isWrapper: !excludeWrapper },
            React.createElement(DropdownController, { ddId: ddId, closeDesktopMenu: closeDesktopMenu, closeMenu: closeMenu, openMenu: openMenu, label: label, topRef: topRef }),
            React.createElement(DropdownContents, { id: ddId, dropdownRef: dropdownRef, navAnalytics: navAnalytics, label: label }, children))));
}
function DropdownController({ ddId, closeDesktopMenu, topRef, closeMenu, openMenu, label }) {
    const { activeDropdown, prefix } = useDropdownContext();
    const isOpen = activeDropdown === topRef;
    const labelId = `${prefix}-${label}`;
    const toggleMenu = React.useCallback((event) => {
        if (activeDropdown === topRef) {
            event.preventDefault();
            closeMenu();
        }
        else {
            openMenu(event);
        }
    }, [openMenu, closeMenu, activeDropdown, topRef]);
    const closeOnBlur = React.useCallback(({ currentTarget, relatedTarget }) => {
        var _a;
        if ((_a = currentTarget.parentNode) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) {
            return;
        }
        closeDesktopMenu();
    }, [closeDesktopMenu]);
    React.useEffect(() => {
        if (isOpen) {
            const handler = ({ key }) => {
                if (key === 'Escape') {
                    closeDesktopMenu();
                }
            };
            document.addEventListener('keydown', handler);
            return () => document.removeEventListener('keydown', handler);
        }
        return undefined;
    }, [isOpen, closeDesktopMenu]);
    return (React.createElement("a", { role: "button", href: ".", "aria-expanded": isOpen, "aria-controls": ddId, onBlur: closeOnBlur, ref: topRef, onClick: toggleMenu, onKeyDown: treatSpaceOrEnterAsClick, className: cn({ 'is-open': isOpen }), "data-analytics-link": isOpen ? 'false' : '', "data-analytics-label": label },
        React.createElement("span", { id: labelId }, label),
        React.createElement("svg", { className: "chevron", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 18 30", "aria-hidden": "true" },
            React.createElement("title", null, " arrow"),
            React.createElement("path", { d: "M12,1L26,16,12,31,8,27,18,16,8,5Z", transform: "translate(-8 -1)" }))));
}
function DropdownContents({ id, label, dropdownRef, navAnalytics, children }) {
    return (React.createElement("div", { className: "dropdown-container" },
        React.createElement("ul", { className: "dropdown-menu", id: id, "aria-label": `${label} menu`, ref: dropdownRef, "data-analytics-nav": navAnalytics || undefined }, React.Children.map(children, (c) => (React.createElement("li", null, c))))));
}
